import { PlayerState } from '@blockworld/shared';
import { PlayerSnapshot } from './NetworkClient';

// ============================================================
// POSITION QUEUE FOR OTHER PLAYERS
// - Immediate updates (no buffer delay)
// - forcePosition for block-action sync (with time-based rejection)
// - Falling prediction on global tick (like falling blocks)
// - Air strafe applied immediately
// ============================================================

export class PositionQueue {
    private queue: PlayerSnapshot[] = [];
    private currentPosition: PlayerSnapshot | null = null;
    private readonly MAX_QUEUE_SIZE = 30;
    private lastPredictedTick = 0;
    private forcedUntilTime = 0; // Reject positions until this time passes

    push(snapshot: PlayerSnapshot): void {
        const now = Date.now();

        // Reject positions that arrive within 200ms of a force
        // This prevents stale network data from overwriting synced position
        if (now < this.forcedUntilTime) {
            return;
        }

        // Reject positions that would teleport a falling player backwards (up)
        // This prevents rubber-banding from out-of-order network data
        if (this.currentPosition &&
            this.currentPosition.state === PlayerState.FALLING &&
            snapshot.y < this.currentPosition.y) {
            // Stale position - player is falling, can't go up
            return;
        }

        // Insert in sequence order
        let inserted = false;
        for (let i = this.queue.length - 1; i >= 0; i--) {
            if (this.queue[i].sequence < snapshot.sequence) {
                this.queue.splice(i + 1, 0, snapshot);
                inserted = true;
                break;
            }
        }
        if (!inserted) {
            this.queue.unshift(snapshot);
        }
        while (this.queue.length > this.MAX_QUEUE_SIZE) {
            this.queue.shift();
        }

        // Apply immediately to current position (no queuing delay)
        if (this.queue.length > 0) {
            this.currentPosition = this.queue[this.queue.length - 1];
            // Reset prediction tick when we get authoritative data
            this.lastPredictedTick = Math.floor(now / 100);
        }
    }

    // Force immediate position update (for block action sync)
    // Rejects positions for a short window to prevent stale overwrites
    forcePosition(snapshot: PlayerSnapshot): void {
        this.queue = [];
        this.currentPosition = snapshot;
        // Reject positions for next 200ms (time for old data to flush)
        this.forcedUntilTime = Date.now() + 200;
        // Reset prediction tick so we don't double-fall
        this.lastPredictedTick = Math.floor(Date.now() / 100);
    }

    // Called on global tick - predict falling (with ground check)
    stepOnTick(globalTick: number, canFall: (x: number, y: number, isCrouching: boolean) => boolean): void {
        // Predict falling: if player is FALLING, move them down 1 tile per tick
        // This syncs remote falling players with falling blocks
        if (this.currentPosition &&
            this.currentPosition.state === PlayerState.FALLING &&
            globalTick > this.lastPredictedTick) {

            // Check if there's ground below before predicting fall
            if (canFall(this.currentPosition.x, this.currentPosition.y, this.currentPosition.isCrouching)) {
                this.lastPredictedTick = globalTick;
                // Predict fall
                this.currentPosition = {
                    ...this.currentPosition,
                    y: this.currentPosition.y + 1
                };
            } else {
                // Hit ground - stop falling, change to grounded state
                this.currentPosition = {
                    ...this.currentPosition,
                    state: this.currentPosition.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE
                };
            }
        }
    }

    // Apply immediate air strafe (horizontal movement while falling/rising)
    applyAirStrafe(dir: number): void {
        if (this.currentPosition) {
            this.currentPosition = {
                ...this.currentPosition,
                x: this.currentPosition.x + dir,
                facing: dir
            };
        }
    }

    // Get current discrete position
    getCurrentPosition(): PlayerSnapshot | null {
        return this.currentPosition;
    }
}
